/**
 * Camada central de espelhamento clínico → patient_psylog_events.
 *
 * Regra de arquitetura: cada tabela especializada (medication_dose_logs,
 * patient_vital_signs, session_checkins) continua sendo a FONTE DA VERDADE.
 * `patient_psylog_events` é apenas o ÍNDICE TEMPORAL unificado.
 *
 * Idempotência: a chave é (source_table, source_record_id). Repetir a operação
 * atualiza o mesmo evento, nunca duplica.
 */
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

export const CLINICAL_TIMEZONE = "America/Sao_Paulo";

export type ClinicalEventType =
  | "medication_ingestion"
  | "substance_exposure"
  | "meal"
  | "caffeine"
  | "nicotine"
  | "sleep_start"
  | "sleep_end"
  | "vital_sign"
  | "symptom"
  | "checkin"
  | "scale"
  | "adverse_event"
  | "psychosocial_event"
  | "treatment_change"
  | "clinical_note";

export class ClinicalEventMirrorError extends Error {
  sourceTable: string;
  sourceRecordId: string;
  constructor(sourceTable: string, sourceRecordId: string, message: string) {
    super(
      `O registro foi salvo, mas o evento da timeline falhou (${sourceTable}): ${message}`,
    );
    this.name = "ClinicalEventMirrorError";
    this.sourceTable = sourceTable;
    this.sourceRecordId = sourceRecordId;
  }
}

export function clinicalDay(iso: string, timeZone = CLINICAL_TIMEZONE): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
  // en-CA => YYYY-MM-DD
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Retroativo = evento significativamente anterior ao momento do registro (> 90 min). */
export function isRetroactive(eventAt: string, createdAt: string | Date = new Date()): boolean {
  const ev = new Date(eventAt).getTime();
  const cr = new Date(createdAt).getTime();
  if (!Number.isFinite(ev) || !Number.isFinite(cr)) return false;
  return cr - ev > 90 * 60 * 1000;
}

export function dedupeKey(sourceTable: string, sourceRecordId: string): string {
  return `${sourceTable}:${sourceRecordId}`;
}

function compactMetadata(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) continue;
    out[key] = value;
  }
  return out;
}

export interface MirrorEventInput {
  patient_id: string;
  source_table: string;
  source_record_id: string;
  event_at: string;
  event_type: ClinicalEventType;
  title: string;
  description?: string | null;
  intensity_0_10?: number | null;
  intensity_0_100?: number | null;
  linked_medication_id?: string | null;
  linked_dose_id?: string | null;
  session_id?: string | null;
  created_at?: string | null;
  data_origin?: string;
  entered_by_role?: string;
  confidence_level?: string;
  metadata?: Record<string, unknown>;
}

/** Cria ou atualiza (upsert lógico) o evento espelho de um registro clínico. */
export async function mirrorClinicalEvent(user: User | null | undefined, input: MirrorEventInput) {
  if (!user?.id) throw new Error("Sessão expirada. Faça login antes de gravar dados clínicos.");
  const eventAt = new Date(input.event_at).toISOString();

  const payload = {
    owner_id: user.id,
    patient_id: input.patient_id,
    event_at: eventAt,
    event_type: input.event_type,
    title: input.title,
    description: input.description ?? null,
    intensity_0_10: input.intensity_0_10 ?? null,
    intensity_0_100: input.intensity_0_100 ?? null,
    linked_medication_id: input.linked_medication_id ?? null,
    linked_dose_id: input.linked_dose_id ?? null,
    linked_session_id: input.session_id ?? null,
    session_id: input.session_id ?? null,
    source_table: input.source_table,
    source_id: input.source_record_id,
    source_record_id: input.source_record_id,
    dedupe_key: dedupeKey(input.source_table, input.source_record_id),
    clinical_day: clinicalDay(eventAt),
    timezone: CLINICAL_TIMEZONE,
    is_retroactive: isRetroactive(eventAt, input.created_at ?? new Date()),
    data_origin: input.data_origin ?? "app",
    entered_by_role: input.entered_by_role ?? "clinician",
    confidence_level: input.confidence_level ?? "registrado",
    metadata: compactMetadata(input.metadata ?? {}),
    deleted_at: null,
    deleted_by: null,
    delete_reason: null,
  };

  const client = supabase as any;
  const { data: existing, error: findError } = await client
    .from("patient_psylog_events")
    .select("id")
    .eq("owner_id", user.id)
    .eq("source_table", input.source_table)
    .eq("source_record_id", input.source_record_id)
    .limit(1)
    .maybeSingle();

  if (findError) throw new ClinicalEventMirrorError(input.source_table, input.source_record_id, findError.message);

  if (existing?.id) {
    const { error } = await client.from("patient_psylog_events").update(payload).eq("id", existing.id);
    if (error) throw new ClinicalEventMirrorError(input.source_table, input.source_record_id, error.message);
    return { id: existing.id as string, created: false };
  }

  const { data, error } = await client
    .from("patient_psylog_events")
    .insert(payload)
    .select("id")
    .single();
  if (error) throw new ClinicalEventMirrorError(input.source_table, input.source_record_id, error.message);
  return { id: data.id as string, created: true };
}

/** Soft-delete do evento espelho (nunca apaga fisicamente). */
export async function softDeleteMirroredEvent(
  user: User | null | undefined,
  sourceTable: string,
  sourceRecordId: string,
  reason = "registro de origem removido",
) {
  if (!user?.id) return;
  const client = supabase as any;
  const { error } = await client
    .from("patient_psylog_events")
    .update({ deleted_at: new Date().toISOString(), deleted_by: user.id, delete_reason: reason, dedupe_key: null })
    .eq("owner_id", user.id)
    .eq("source_table", sourceTable)
    .eq("source_record_id", sourceRecordId)
    .is("deleted_at", null);
  if (error) throw new ClinicalEventMirrorError(sourceTable, sourceRecordId, error.message);
}

// ---------------------------------------------------------------------------
// Espelhos específicos
// ---------------------------------------------------------------------------

function doseTitle(row: any): string {
  const name = row.formulation_name || row.substance_name || "Dose";
  const amount = row.dose_text || (row.dose_amount != null ? `${row.dose_amount} ${row.dose_unit ?? ""}`.trim() : null);
  return amount ? `${name} ${amount}` : String(name);
}

export async function mirrorDoseLogEvent(user: User | null | undefined, row: any) {
  const isPrescribed = !row.log_type || !/abuso|uso_problematico|nao_prescrit/i.test(String(row.log_type));
  return mirrorClinicalEvent(user, {
    patient_id: row.patient_id,
    source_table: "medication_dose_logs",
    source_record_id: row.id,
    event_at: row.actual_time,
    created_at: row.created_at,
    event_type: isPrescribed ? "medication_ingestion" : "substance_exposure",
    title: doseTitle(row),
    description: row.perceived_effect_text || row.notes_clinician || row.notes_patient || null,
    intensity_0_100: row.benefit_0_100 ?? null,
    linked_medication_id: row.patient_medication_id ?? null,
    linked_dose_id: row.id,
    session_id: row.session_id ?? null,
    metadata: {
      substance_id: row.substance_id,
      substance_name: row.substance_name,
      formulation_id: row.formulation_id,
      formulation: row.formulation_name ?? row.formulation,
      route: row.route,
      dose_amount: row.dose_amount,
      dose_unit: row.dose_unit,
      beverage_name: row.beverage_name,
      ethanol_grams: row.ethanol_grams,
      beverage_consumed_ml: row.beverage_consumed_ml,
      beverage_abv_percent: row.beverage_abv_percent,
      powder_mass_mg: row.powder_mass_mg,
      purity_status: row.purity_status,
      purity_percent: row.purity_percent,
      purity_source: row.purity_source,
      estimated_active_mg: row.estimated_active_mg,
      material_profile_id: row.material_profile_id,
      material_profile_snapshot: row.material_profile_snapshot,
      material_profile_risk_interpretation: row.material_profile_risk_interpretation,
      log_type: row.log_type,
      dose_goal: row.dose_goal,
      taken_with_food: row.taken_with_food,
      caffeine_near_dose_mg: row.caffeine_near_dose_mg,
      sleep_deprivation_at_dose_0_10: row.sleep_deprivation_at_dose_0_10,
      use_classification: isPrescribed ? "prescrito" : "não prescrito / problemático",
    },
  });
}

export async function mirrorVitalSignEvent(
  user: User | null | undefined,
  row: any,
  sourceTable: "vital_signs" | "patient_vital_signs" = row?.__source_table ?? "vital_signs",
) {
  const systolic = row.systolic_mm_hg ?? row.systolic_bp ?? null;
  const diastolic = row.diastolic_mm_hg ?? row.diastolic_bp ?? null;
  const heartRate = row.heart_rate_bpm ?? row.heart_rate ?? null;
  const spo2 = row.spo2_percent ?? row.oxygen_saturation ?? null;
  const respiratoryRate = row.respiratory_rate_bpm ?? null;
  const temperature = row.temperature_celsius ?? null;
  const weight = row.weight_kg ?? null;
  const parts = [
    heartRate != null ? `FC ${heartRate} bpm` : null,
    systolic != null && diastolic != null ? `PA ${systolic}/${diastolic} mmHg` : null,
    spo2 != null ? `SpO₂ ${spo2}%` : null,
    respiratoryRate != null ? `FR ${respiratoryRate} irpm` : null,
    temperature != null ? `${temperature} °C` : null,
    weight != null ? `${weight} kg` : null,
  ].filter(Boolean);

  return mirrorClinicalEvent(user, {
    patient_id: row.patient_id,
    source_table: sourceTable,
    source_record_id: row.id,
    event_at: row.measured_at,
    created_at: row.created_at,
    event_type: "vital_sign",
    title: parts.join(" · ") || "Sinais vitais",
    description: row.clinical_note || row.notes || row.context || null,
    linked_dose_id: row.linked_dose_id ?? null,
    session_id: row.session_id ?? null,
    metadata: {
      systolic_mm_hg: systolic,
      diastolic_mm_hg: diastolic,
      heart_rate_bpm: heartRate,
      spo2_percent: spo2,
      respiratory_rate_bpm: respiratoryRate,
      temperature_celsius: temperature,
      glucose_mg_dl: row.glucose_mg_dl,
      weight_kg: weight,
      posture: row.posture ?? row.context_structured?.posture,
      measurement_source: row.measurement_source ?? row.context_structured?.measurement_source,
      context: row.context,
      context_structured: row.context_structured,
      red_flags: row.red_flags,
      linked_dose_id: row.linked_dose_id,
      source_schema: sourceTable,
    },
  });
}

const CHECKIN_AXES = [
  "focus_0_10", "energy_0_10", "motivation_0_10", "anxiety_0_10", "irritability_0_10",
  "impulsivity_0_10", "rumination_0_10", "craving_0_10", "withdrawal_0_10", "sedation_0_10",
  "insomnia_0_10", "sleep_quality_0_10", "paranoia_0_10", "ideas_of_reference_0_10",
  "cognitive_overload_0_10", "mood_0_10", "anhedonia_0_10", "appetite_0_10", "cardiovascular_0_10",
];

export async function mirrorCheckinEvent(user: User | null | undefined, row: any) {
  const axes: Record<string, number> = {};
  for (const key of CHECKIN_AXES) {
    const value = row[key];
    if (value != null) axes[key.replace("_0_10", "")] = Number(value);
  }
  const eventAt = row.checkin_at ?? row.created_at ?? new Date().toISOString();
  return mirrorClinicalEvent(user, {
    patient_id: row.patient_id,
    source_table: "session_checkins",
    source_record_id: row.id,
    event_at: eventAt,
    created_at: row.created_at,
    event_type: row.adverse_event ? "adverse_event" : "checkin",
    title: row.adverse_event ? "Evento adverso no check-in" : "Check-in clínico",
    description: row.patient_report || row.physician_observation || row.adverse_event || null,
    session_id: row.session_id ?? null,
    metadata: { axes, trigger: row.trigger_text, context: row.context_text, adverse_event: row.adverse_event },
  });
}
